import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider, useTheme, Topbar, PageHeader, LoadingScreen, EmptyState } from '../shared/DashWidgets';
import Sidebar from '../shared/Sidebar';
import { buildStyles, COLORS } from '../shared/DashTheme';
import api from '../../services/api';

const ROLE_COLOR = {
  student:   COLORS.blue,
  faculty:   COLORS.amber,
  admin:     COLORS.purple,
  authority: COLORS.green,
};
const ROLE_ICON = { student: '🎒', faculty: '👨‍🏫', admin: '🛡️', authority: '⭐' };
const ALL_ROLES = ['all', 'student', 'faculty', 'admin', 'authority'];

function ProfileModal({ user, onClose, s }) {
  const color = ROLE_COLOR[user.role] || COLORS.purple;
  const icon  = ROLE_ICON[user.role]  || '👤';
  const roleLabel = user.role
    + (user.admin_type ? ' (' + user.admin_type + ')' : '')
    + (user.authority_type ? ' (' + user.authority_type + ')' : '');
  const rows = [
    { label: 'Email',      value: user.email },
    { label: 'Role',       value: roleLabel },
    { label: 'Department', value: user.department || '—' },
    { label: 'Status',     value: user.is_active === false ? 'Inactive' : 'Active' },
    { label: 'Joined',     value: user.created_at
        ? new Date(user.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
        : '—' },
  ];
  const stats = [];
  if (user.role === 'admin') {
    stats.push(
      { label: 'Verified',   value: user.verified_count   ?? 0, color: COLORS.blue  },
      { label: 'Rejected',   value: user.rejected_count   ?? 0, color: '#f87171'    },
      { label: 'Negligence', value: user.negligence_count ?? 0, color: COLORS.amber },
    );
  } else if (user.role === 'authority') {
    stats.push(
      { label: 'Resolved', value: user.resolved_count ?? 0, color: COLORS.green },
      { label: 'Assigned', value: user.assigned_count ?? 0, color: COLORS.cyan  },
    );
  } else {
    stats.push({ label: 'Complaints', value: user.complaint_count ?? 0, color: COLORS.purple });
  }
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}
      onClick={onClose}>
      <div style={{ ...s.card, width: '100%', maxWidth: 420, padding: 0,
          overflow: 'hidden', animation: 'scaleIn 0.2s ease-out' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ padding: '24px 24px 18px',
            background: `linear-gradient(135deg,${color}22,${color}08)`,
            borderBottom: `1px solid ${color}33`, position: 'relative' }}>
          <button onClick={onClose} style={{ position: 'absolute', top: 12, right: 12,
              background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: 8, color: '#fff', cursor: 'pointer', width: 28, height: 28,
              fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            ×
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%',
                background: `linear-gradient(135deg,${color}55,${color}22)`,
                border: `2px solid ${color}66`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 22, fontWeight: 900, color: '#fff', flexShrink: 0 }}>
              {user.name?.[0]?.toUpperCase() || '?'}
            </div>
            <div>
              <div style={{ fontSize: 17, fontWeight: 800, color: '#fff' }}>{user.name}</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>{user.email}</div>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 7,
                  fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
                  background: `${color}33`, color: color, border: `1px solid ${color}55` }}>
                {icon} {user.role.toUpperCase()}
              </span>
            </div>
          </div>
        </div>
        {stats.length > 0 && (
          <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
            {stats.map(st => (
              <div key={st.label} style={{ flex: 1, padding: '12px 0', textAlign: 'center',
                  borderRight: '1px solid rgba(255,255,255,0.07)' }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: st.color }}>{st.value}</div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 2,
                    textTransform: 'uppercase', letterSpacing: 0.5 }}>{st.label}</div>
              </div>
            ))}
          </div>
        )}
        <div style={{ padding: '16px 24px 22px' }}>
          {rows.map(({ label, value }) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', padding: '8px 0',
                borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', fontWeight: 600,
                  textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</span>
              <span style={{ fontSize: 13, color: '#fff', fontWeight: 600,
                  maxWidth: 220, textAlign: 'right', wordBreak: 'break-all' }}>{value}</span>
            </div>
          ))}
          {(user.negligence_count > 0) && (
            <div style={{ marginTop: 12, padding: '7px 12px', borderRadius: 8,
                background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)',
                fontSize: 12, color: '#fca5a5', display: 'flex', alignItems: 'center', gap: 8 }}>
              ⚠ {user.negligence_count} negligence flag{user.negligence_count !== 1 ? 's' : ''} recorded
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RoleBadge({ role }) {
  const color = ROLE_COLOR[role] || 'rgba(255,255,255,0.4)';
  return (
    <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, fontWeight: 700,
        background: `${color}22`, border: `1px solid ${color}55`, color }}>
      {ROLE_ICON[role] || '👤'} {role}
    </span>
  );
}

function UserCard({ u, index, onClick }) {
  const color = ROLE_COLOR[u.role] || 'rgba(255,255,255,0.3)';
  return (
    <div onClick={onClick} className="ur-card-hover" style={{
      display: 'grid', gridTemplateColumns: '40px 1fr auto',
      alignItems: 'center', gap: 14, padding: '14px 18px',
      borderRadius: 12, background: 'rgba(255,255,255,0.06)',
      border: '1px solid rgba(255,255,255,0.1)',
      borderLeft: `3px solid ${color}`,
      animation: 'fadeUp 0.35s ease-out both',
      animationDelay: `${index * 0.03}s`,
      cursor: 'pointer',
    }}>
      <div style={{ width: 40, height: 40, borderRadius: 10,
          background: `linear-gradient(135deg,${color}44,${color}22)`,
          border: `1px solid ${color}55`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 16, fontWeight: 800, color }}>
        {u.name?.[0]?.toUpperCase() || '?'}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#fff',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.name}</div>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 2,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.email}</div>
        {u.department && (
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
            {u.department}{u.admin_type ? ` · ${u.admin_type} admin` : ''}
          </div>
        )}
      </div>
      <RoleBadge role={u.role} />
    </div>
  );
}

function UsersListInner() {
  const { theme } = useTheme();
  const s = buildStyles(theme);
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  React.useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get('/authority/users')
      .then(res => setUsers(Array.isArray(res.data) ? res.data : []))
      .catch(e => setError(e.response?.data?.error || 'Failed to load users'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingScreen />;

  const counts = ALL_ROLES.reduce((acc, r) => {
    acc[r] = r === 'all' ? users.length : users.filter(u => u.role === r).length;
    return acc;
  }, {});

  const filtered = users.filter(u => {
    const matchRole = roleFilter === 'all' || u.role === roleFilter;
    const q = search.toLowerCase();
    const matchSearch = !q
      || u.name?.toLowerCase().includes(q)
      || u.email?.toLowerCase().includes(q)
      || u.department?.toLowerCase().includes(q);
    return matchRole && matchSearch;
  });

  return (
    <div style={s.page}>
      <div style={s.bgGrid} />
      <div style={s.bgOrb1} />
      <div style={s.bgOrb2} />
      <div style={s.bgOrb3} />
      <div style={s.layout}>
        {sidebarOpen && <Sidebar onToggle={() => setSidebarOpen(o => !o)} mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />}
        <div style={{ ...s.main, marginLeft: isMobile ? 0 : (sidebarOpen ? 248 : 0), transition: 'margin-left 0.3s ease' }}>
          <Topbar title="All Users" subtitle="University-wide user directory"
            onToggleSidebar={() => setSidebarOpen(o => !o)}
            onMobileMenu={() => setMobileOpen(true)}
            actions={
              <button className="ur-btn-hover" style={s.btnSecondary}
                onClick={() => navigate('/dashboard')}>Dashboard</button>
            }
          />
          <div style={s.content}>
            <PageHeader icon="👥" title="User Directory"
              subtitle="All registered users across the university"
              stats={[
                { label: 'Total',    value: counts.all     },
                { label: 'Students', value: counts.student  },
                { label: 'Faculty',  value: counts.faculty  },
                { label: 'Admins',   value: counts.admin    },
              ]}
            />
            {error && <div style={s.alertErr}>{error}</div>}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20, alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {ALL_ROLES.map(r => {
                  const color = ROLE_COLOR[r] || COLORS.purple;
                  const active = roleFilter === r;
                  return (
                    <button key={r} onClick={() => setRoleFilter(r)} style={{
                      padding: '6px 16px', borderRadius: 20, cursor: 'pointer',
                      fontSize: 12, fontWeight: 600, transition: 'all 0.2s',
                      border: `1px solid ${active ? color : 'rgba(255,255,255,0.15)'}`,
                      background: active ? `${color}33` : 'rgba(255,255,255,0.05)',
                      color: active ? color : 'rgba(255,255,255,0.6)',
                    }}>
                      {r === 'all' ? 'All' : `${ROLE_ICON[r]} ${r.charAt(0).toUpperCase() + r.slice(1)}`}
                      {' '}({counts[r] || 0})
                    </button>
                  );
                })}
              </div>
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search name, email, department..."
                style={{ marginLeft: 'auto', padding: '8px 14px', borderRadius: 10,
                  background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
                  color: '#fff', fontSize: 13, outline: 'none', minWidth: 240 }}
              />
            </div>
            {filtered.length === 0
              ? <EmptyState text="No users found" sub="Try adjusting your filters" />
              : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {filtered.map((u, i) => (
                    <UserCard key={u.id} u={u} index={i} onClick={() => setSelected(u)} />
                  ))}
                </div>
              )
            }
          </div>
        </div>
      </div>
      {selected && <ProfileModal user={selected} onClose={() => setSelected(null)} s={s} />}
    </div>
  );
}

export default function UsersList() {
  return <ThemeProvider><UsersListInner /></ThemeProvider>;
}
